// =============================================================================
// 2026-09-22 (user request): util bersama untuk drill-down pengeluaran
// (Laporan Keuangan → Pengeluaran → Detail Pengeluaran per minggu).
// mondayOf: kunci minggu (Senin) ISO "YYYY-MM-DD" dari tanggal apa pun.
// weekLabelOf: label Indonesia "Minggu 27 Jul – 2 Agu 2026".
// buildUnifiedExpenses: SATU builder baris pengeluaran gabungan
// (expense_entries per project + petty cash) — logika IDENTIK dengan
// PengeluaranTab supaya angka di halaman detail selalu sama dengan tab.
// =============================================================================

const ID_MONTHS = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];

/** Kunci minggu = tanggal Senin (ISO) dari tanggal input. null bila tak valid. */
export function mondayOf(dateStr) {
  if (!dateStr) return null;
  const d = new Date(String(dateStr).slice(0, 10) + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  const diff = (d.getDay() + 6) % 7; // hari sejak Senin (0=Senin)
  d.setDate(d.getDate() - diff);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Label periode minggu Indonesia dari kunci Senin. */
export function weekLabelOf(weekKey) {
  if (!weekKey || weekKey === "tanpa-tanggal") return "Tanpa Tanggal";
  const d = new Date(weekKey + "T00:00:00");
  if (isNaN(d.getTime())) return weekKey;
  const end = new Date(d);
  end.setDate(end.getDate() + 6);
  const f = (x) => `${x.getDate()} ${ID_MONTHS[x.getMonth()]}`;
  return `Minggu ${f(d)} – ${f(end)} ${end.getFullYear()}`;
}

// 2026-09-22 (user request P1): entri expense_entries yang sebenarnya berasal
// dari petty cash — tag `finance_source = "petty_cash_carry"` ATAU pola note
// "Pengeluaran minggu YYYY-Wxxx" (Buku Pengeluaran Mingguan) — diklasifikasi
// sebagai sumber "petty" di tab Pengeluaran (pindah label/tab SAJA; total
// proyek di Ringkasan/Monitoring tidak berubah).
// 2026-10-14: toleran 2-3 digit (W37 vs W038 legacy) — format kanonik 2 digit.
export const PETTY_CARRY_NOTE_RE = /^Pengeluaran minggu \d{4}-W\d{2,3}/;

export function isPettyCarryEntry(e) {
  return e?.finance_source === "petty_cash_carry" || PETTY_CARRY_NOTE_RE.test(String(e?.note || ""));
}

/**
 * Baris pengeluaran gabungan (expense + petty cash) dengan klasifikasi
 * sub-project dari `wo_number` ("MAIN:<uuid>" | "VO:<nama>" | "<nomor WO>").
 * Dipakai PengeluaranProjectPage & PengeluaranMingguPage.
 * `recordKind` = sumber record asli ("expense" = expense_entries — bisa
 * edit/hapus); `source` = label tampilan/filter (carry → "petty").
 */
export function buildUnifiedExpenses(projects, pettyCash) {
  const list = [];
  for (const p of projects || []) {
    for (const e of p.expense_entries || []) {
      const wn = String(e.wo_number || "").trim();
      let subKind = "none";
      let subLabel = "";
      if (wn.startsWith("MAIN:")) { subKind = "main"; subLabel = "Main"; }
      else if (wn.startsWith("VO:")) { subKind = "vo"; subLabel = `VO · ${wn.slice(3)}`; }
      else if (wn) { subKind = "wo"; subLabel = `WO ${wn}`; }
      const carry = isPettyCarryEntry(e);
      list.push({
        id: `exp-${e.id}`,
        date: e.date,
        source: carry ? "petty" : "expense",
        recordKind: "expense",
        project: p,
        note: e.note,
        amount: Number(e.amount) || 0,
        by: e.recorded_by_name || e.edited_by_name || "-",
        subKind,
        subLabel,
        raw: { ...e, __project_id: p.id },
      });
    }
  }
  for (const p of pettyCash || []) {
    if (p.deleted_at) continue;
    const actual = Number(p.actual_amount) || 0;
    const estimated = (p.items || []).reduce((s, it) => s + (Number(it.unit_price) || 0) * (Number(it.qty) || 1), 0);
    list.push({
      id: `pc-${p.id}`,
      date: p.actual_transaction_date || p.transferred_at || p.period_end || p.created_at,
      source: "petty",
      recordKind: "petty",
      project: null,
      note: `Petty ${p.week_label || ""} · ${(p.items || []).length} item · ${p.status || "-"}`,
      amount: actual > 0 ? actual : estimated,
      by: p.owner_name || p.created_by_name || "-",
      subKind: "none",
      subLabel: "",
      raw: p,
    });
  }
  return list;
}

/** Saring baris milik satu grup head (induk project + sub VO/WO) atau ops-kantor.
 * 2026-09-22: filter berdasar PROJECT (bukan source) supaya entri carry
 * (label "petty" tapi tetap milik project) tidak hilang dari drill-down. */
export function rowsForHead(unified, pid) {
  if (pid === "ops-kantor") return unified.filter((r) => r.project == null);
  return unified.filter(
    (r) => r.project && (r.project?.parent_project_id || r.project?.id) === pid,
  );
}
